import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { BoardApiService } from '../../services/board-api.service';
import { DeleteAccountModal } from '../../components/delete-account-modal/delete-account-modal';

@Component({
  selector: 'app-profile',
  imports: [FormsModule, DeleteAccountModal],
  templateUrl: './profile.html',
})
export class Profile {
  private auth = inject(AuthService);
  private boardApi = inject(BoardApiService);
  private router = inject(Router);

  protected readonly user = this.auth.user;

  protected readonly name = signal(this.auth.user()?.name ?? '');
  protected readonly currentPassword = signal('');
  protected readonly newPassword = signal('');
  protected readonly confirmPassword = signal('');
  protected readonly submitting = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly success = signal<string | null>(null);

  protected readonly showDeleteModal = signal(false);
  protected readonly deleting = signal(false);
  protected readonly deleteError = signal<string | null>(null);

  protected readonly resending = signal(false);
  protected readonly resendSent = signal(false);
  protected readonly resendError = signal<string | null>(null);

  // null until /list-accounts answers. The password blocks stay hidden while
  // unknown so a social-only user never sees them flash in and disappear.
  protected readonly hasPassword = signal<boolean | null>(null);

  constructor() {
    void this.loadAccounts();
  }

  private async loadAccounts(): Promise<void> {
    const { accounts, error } = await this.auth.listAccounts();
    // On failure keep the password UI hidden rather than offering a form that
    // cannot work; deletion falls back to the fresh-session path either way.
    if (error) return;
    this.hasPassword.set(accounts.some((a) => a.providerId === 'credential'));
  }

  protected async resendVerification(): Promise<void> {
    if (this.resending()) return;
    this.resendError.set(null);
    this.resending.set(true);
    const { error } = await this.auth.resendVerificationEmail();
    this.resending.set(false);
    if (error) {
      this.resendError.set(error);
      return;
    }
    this.resendSent.set(true);
  }

  protected async submit(): Promise<void> {
    if (this.submitting()) return;
    this.error.set(null);
    this.success.set(null);

    const name = this.name().trim();
    const currentPassword = this.currentPassword();
    const newPassword = this.newPassword();

    if (!name) {
      this.error.set('Display name cannot be empty.');
      return;
    }
    const changingPassword = newPassword.length > 0;
    if (changingPassword && !currentPassword) {
      this.error.set('Enter your current password to set a new one.');
      return;
    }
    if (changingPassword && newPassword !== this.confirmPassword()) {
      this.error.set('New password and verification do not match.');
      return;
    }
    const nameChanged = name !== (this.user()?.name ?? '');
    if (!nameChanged && !changingPassword) {
      this.success.set('Nothing to save.');
      return;
    }

    this.submitting.set(true);
    const saved: string[] = [];

    if (nameChanged) {
      const { error } = await this.auth.updateUser({ name });
      if (error) {
        this.submitting.set(false);
        this.error.set(error);
        return;
      }
      saved.push('display name');
    }

    if (changingPassword) {
      const { error } = await this.auth.changePassword({ currentPassword, newPassword });
      if (error) {
        this.submitting.set(false);
        // Name may already be saved at this point; surface both facts.
        this.error.set(saved.length ? `Display name saved, but the password change failed: ${error}` : error);
        return;
      }
      saved.push('password');
      this.currentPassword.set('');
      this.newPassword.set('');
      this.confirmPassword.set('');
    }

    this.submitting.set(false);
    this.success.set(`Saved ${saved.join(' and ')}.`);
  }

  protected openDeleteModal(): void {
    this.deleteError.set(null);
    this.showDeleteModal.set(true);
  }

  protected closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.deleteError.set(null);
    // If deletion partially succeeded (auth account gone, board cleanup failed)
    // the session was already cleared; don't leave the user on a dead profile page.
    if (!this.auth.isAuthenticated()) {
      void this.router.navigateByUrl('/');
    }
  }

  /**
   * Account deletion order matters: the auth account goes first, and only if
   * that succeeds is the Go API called to wipe board data. The JWT must be
   * cached BEFORE the auth account dies - it stays valid (stateless, JWKS
   * verified) but can no longer be issued afterwards.
   */
  protected async confirmDelete(password: string): Promise<void> {
    if (this.deleting()) return;
    this.deleteError.set(null);
    this.deleting.set(true);

    const token = await this.auth.getToken();
    if (!token) {
      this.deleting.set(false);
      this.deleteError.set('Could not prepare account deletion. Please try again.');
      return;
    }

    const { error, code } = await this.auth.deleteAccount(
      password ? { password } : {},
    );
    if (error) {
      this.deleting.set(false);
      // Social-only users delete without a password, which better-auth allows
      // only on a session younger than 24h. Say so in words they can act on.
      this.deleteError.set(
        code === 'SESSION_EXPIRED'
          ? 'For your security, sign out and sign back in, then delete your account.'
          : error,
      );
      return;
    }

    try {
      await firstValueFrom(this.boardApi.deleteAccountData());
    } catch {
      // The auth account is already gone; the user cannot retry this call.
      this.deleting.set(false);
      this.deleteError.set(
        'Your account and login were deleted, but removing your board data failed. Please contact support so we can finish the cleanup.',
      );
      this.auth.clearSession();
      return;
    }

    this.auth.clearSession();
    this.deleting.set(false);
    void this.router.navigateByUrl('/');
  }
}
